import {
  WORKFLOW_DEBOUNCE_WEEK_STARTS,
  WORKFLOW_DEBOUNCE_WINDOWS,
} from '@cotera/api';
import { Assert } from '@cotera/utilities';
import { DateTime, IANAZone } from 'luxon';
import {
  isAddressableUpstreamFieldPath,
  normalizeUpstreamFieldPath,
  resolveUpstreamFieldValue,
} from './upstream-field-path';

/**
 * A debounce node lets a value through once per window and suppresses it for
 * the rest of that window. The config and the window arithmetic live here so
 * the runner, the API schema and the editor all agree on what "once a week"
 * means — the boundary is a calendar truncation in a named timezone, not a
 * rolling duration since the last pass.
 *
 * Calendar rather than rolling is deliberate: the business rule these express
 * is "alert once a week, they have until the end of that week to fix it", which
 * is a boundary everyone can name. A rolling window would also need per-key
 * last-fired state to be read before it could be written, giving up the
 * insert-is-the-decision property the claims table relies on.
 */

/** Window sizes a debounce node can truncate to. Persisted in node `data`. */
export const DEBOUNCE_WINDOWS = WORKFLOW_DEBOUNCE_WINDOWS;
export type DebounceWindow = (typeof DEBOUNCE_WINDOWS)[number];

export function isDebounceWindow(value: unknown): value is DebounceWindow {
  return (
    typeof value === 'string' &&
    (DEBOUNCE_WINDOWS as readonly string[]).includes(value)
  );
}

/** Plain-language labels, written to read in a sentence ("once a week"). */
export const DEBOUNCE_WINDOW_LABELS: Record<DebounceWindow, string> = {
  hour: 'an hour',
  day: 'a day',
  week: 'a week',
  month: 'a month',
};

/**
 * Which day a week-window starts on. Only meaningful for `window: 'week'`, but
 * always present on a parsed config so the runner never branches on undefined.
 */
export const DEBOUNCE_WEEK_STARTS = WORKFLOW_DEBOUNCE_WEEK_STARTS;
export type DebounceWeekStart = (typeof DEBOUNCE_WEEK_STARTS)[number];

export function isDebounceWeekStart(
  value: unknown
): value is DebounceWeekStart {
  return value === 'monday' || value === 'sunday';
}

/**
 * UTC rather than the server's zone: a default that silently depends on where
 * the process runs would put the boundary somewhere different in every
 * environment. Editors seed the viewer's own zone explicitly.
 */
export const DEFAULT_DEBOUNCE_TIMEZONE = 'UTC';
export const DEFAULT_DEBOUNCE_WINDOW: DebounceWindow = 'week';
export const DEFAULT_DEBOUNCE_WEEK_START: DebounceWeekStart = 'monday';

export function isValidDebounceTimezone(value: unknown): value is string {
  return typeof value === 'string' && IANAZone.isValidZone(value);
}

/** A debounce node's validated config. */
export type DebounceConfig = {
  /**
   * The field identifying what is being debounced — a JSON Pointer into the
   * upstream output (`/subscriptionId`) or a `NodeName/pointer` reference.
   *
   * Deliberately a single path. The claim key is stored verbatim so an operator
   * clearing one can recognise it, and composing several fields into one key is
   * what an upstream mapper node is for — which also avoids inventing a
   * delimiter that a value could itself contain.
   */
  readonly keyPath: string;
  readonly window: DebounceWindow;
  /** IANA zone the window boundary is computed in. */
  readonly timezone: string;
  readonly weekStartsOn: DebounceWeekStart;
};

export type ParseDebounceConfigResult =
  | { readonly ok: true; readonly config: DebounceConfig }
  | { readonly ok: false; readonly error: string };

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

/**
 * Validates persisted node `data` into a {@link DebounceConfig}.
 *
 * Every error names the field and what was wrong with it: an invalid config
 * fails the step with this message, and it is the only thing the person fixing
 * it will see.
 */
export function parseDebounceConfig(data: unknown): ParseDebounceConfigResult {
  const record = asRecord(data);
  if (record === null) {
    return { ok: false, error: 'Debounce node is missing configuration.' };
  }

  const rawKeyPath = record['keyPath'];
  if (rawKeyPath !== undefined && typeof rawKeyPath !== 'string') {
    return { ok: false, error: 'Debounce key must be a field path.' };
  }
  const keyPath = normalizeUpstreamFieldPath(rawKeyPath ?? '');
  if (keyPath === '') {
    return {
      ok: false,
      error:
        'Debounce node needs a key — the field identifying what to debounce (e.g. "/subscriptionId").',
    };
  }
  if (!isAddressableUpstreamFieldPath(keyPath)) {
    return {
      ok: false,
      error: `Debounce key must be a JSON Pointer ("/field") or an upstream reference ("Node/field") (got "${keyPath}").`,
    };
  }

  const rawWindow = record['window'];
  if (rawWindow !== undefined && !isDebounceWindow(rawWindow)) {
    return {
      ok: false,
      error: `Debounce window must be one of ${DEBOUNCE_WINDOWS.join(
        ', '
      )} (got "${String(rawWindow)}").`,
    };
  }
  const window = rawWindow ?? DEFAULT_DEBOUNCE_WINDOW;

  const rawTimezone = record['timezone'];
  if (rawTimezone !== undefined && !isValidDebounceTimezone(rawTimezone)) {
    return {
      ok: false,
      error: `Debounce timezone must be an IANA zone like "America/New_York" (got "${String(
        rawTimezone
      )}").`,
    };
  }
  const timezone = rawTimezone ?? DEFAULT_DEBOUNCE_TIMEZONE;

  const rawWeekStart = record['weekStartsOn'];
  if (rawWeekStart !== undefined && !isDebounceWeekStart(rawWeekStart)) {
    return {
      ok: false,
      error: `Debounce week start must be "monday" or "sunday" (got "${String(
        rawWeekStart
      )}").`,
    };
  }
  const weekStartsOn = rawWeekStart ?? DEFAULT_DEBOUNCE_WEEK_START;

  return { ok: true, config: { keyPath, window, timezone, weekStartsOn } };
}

/**
 * The start of the window containing `now`, as an instant.
 *
 * Truncation happens in the config's timezone and the result is kept as an
 * instant, so two nodes debouncing the same key in different zones claim
 * different buckets — which is what having a timezone at all is for.
 */
export function computeDebounceBucketStart(params: {
  now: Date;
  config: DebounceConfig;
}): Date {
  const { now, config } = params;
  const local = DateTime.fromJSDate(now, { zone: config.timezone });
  if (!local.isValid) {
    // parseDebounceConfig rejects unknown zones, so reaching here means the
    // instant itself was unusable.
    throw new Error(
      `Debounce could not place ${now.toISOString()} in timezone "${
        config.timezone
      }".`
    );
  }

  switch (config.window) {
    case 'hour':
      return local.startOf('hour').toJSDate();
    case 'day':
      return local.startOf('day').toJSDate();
    case 'week': {
      // Luxon's weekday is ISO: 1 = Monday … 7 = Sunday. Computed explicitly
      // rather than via startOf('week') because that is locale-dependent, and a
      // week boundary that moves with the runtime's locale is exactly the bug
      // this node exists to avoid. Calendar-day arithmetic on the local zone,
      // so a DST transition inside the week does not shift the boundary.
      const daysFromStart =
        config.weekStartsOn === 'monday'
          ? local.weekday - 1
          : local.weekday % 7;
      return local.startOf('day').minus({ days: daysFromStart }).toJSDate();
    }
    case 'month':
      return local.startOf('month').toJSDate();
    default:
      return Assert.unreachable(config.window);
  }
}

/**
 * The start of the window *after* the one containing `now` — i.e. when a key
 * claimed now becomes claimable again.
 *
 * Lives beside {@link computeDebounceBucketStart} so "when does this reopen?"
 * is answered by the same arithmetic that decided the bucket, rather than by a
 * second implementation in whatever surface needs to display it.
 */
export function computeNextDebounceBucketStart(params: {
  now: Date;
  config: DebounceConfig;
}): Date {
  const { config } = params;
  const current = DateTime.fromJSDate(computeDebounceBucketStart(params), {
    zone: config.timezone,
  });
  switch (config.window) {
    case 'hour':
      return current.plus({ hours: 1 }).toJSDate();
    case 'day':
      return current.plus({ days: 1 }).toJSDate();
    case 'week':
      return current.plus({ weeks: 1 }).toJSDate();
    case 'month':
      return current.plus({ months: 1 }).toJSDate();
    default:
      return Assert.unreachable(config.window);
  }
}

export type ResolveDebounceKeyResult =
  | { readonly ok: true; readonly key: string }
  | { readonly ok: false; readonly error: string };

/**
 * Reads the debounce key out of the upstream value.
 *
 * A missing or non-scalar key is an error rather than a pass or a silent stop.
 * There is no safe default: passing would let the same value through on every
 * evaluation forever, and stopping would drop it with no trace. Failing names
 * the path that did not resolve, and inside a for-each only that row's branch
 * fails.
 */
export function resolveDebounceKey(params: {
  upstream: unknown;
  config: DebounceConfig;
  upstreamByNodeId?: Readonly<Record<string, unknown>>;
}): ResolveDebounceKeyResult {
  const { upstream, config, upstreamByNodeId } = params;
  const value = resolveUpstreamFieldValue({
    upstream,
    path: config.keyPath,
    upstreamByNodeId,
  });

  if (value === undefined || value === null) {
    return {
      ok: false,
      error: `Debounce key "${config.keyPath}" is not present in this step's input.`,
    };
  }
  if (
    typeof value !== 'string' &&
    typeof value !== 'number' &&
    typeof value !== 'boolean'
  ) {
    return {
      ok: false,
      error: `Debounce key "${config.keyPath}" must be a single value, not ${
        Array.isArray(value) ? 'a list' : 'an object'
      }.`,
    };
  }
  if (typeof value === 'number' && !Number.isFinite(value)) {
    return {
      ok: false,
      error: `Debounce key "${
        config.keyPath
      }" is not a usable number (got ${String(value)}).`,
    };
  }

  const key = String(value).trim();
  if (key === '') {
    return {
      ok: false,
      error: `Debounce key "${config.keyPath}" is empty in this step's input.`,
    };
  }
  return { ok: true, key };
}

/**
 * One-line summary of what a node does, e.g.
 * `once a week per /subscriptionId`. Shared by the canvas node body, the
 * editor's preview and the run view so all three read identically.
 */
export function describeDebounceConfig(config: DebounceConfig): string {
  return `once ${DEBOUNCE_WINDOW_LABELS[config.window]} per ${config.keyPath}`;
}
